<template>
  <div class="flex flex-col justify-start gap-8">
    <div class="flex flex-col gap-3">
      <p class="text-sm text-jet">
        Select the job levels that apply to this policy and customize their entitlement days and carryover limits.
      </p>

      <div class="line"></div>

      <div class="flex items-center gap-2 mb-3">
        <label
          class="text-sm font-semibold text-darkPurple" 
          :class="isViewMode || isSelectAllDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'"
          @click="!isViewMode && !isSelectAllDisabled && (selectAllLevels = !selectAllLevels)"
        >
          Select All
        </label>
        <c-checkbox
          v-model="selectAllLevels"
          :disabled="isViewMode || isSelectAllDisabled"
          :key="'select-all'"
        />
      </div>

      <div class="flex flex-wrap gap-2">
        <button
          v-for="level in displayLevels"
          :key="level.id"
          type="button"
          @click="toggleLevelSelection(level)"
          :class="['level-pill', { active: isLevelSelected(level.id), 'disabled': level.disabled }]"
          :disabled="isViewMode || level.disabled"
          :title="level.disabled ? 'This level is already assigned to another leave policy' : ''"
        >
          {{ level.name }}
        </button>
      </div>

      <div class="line"></div>

      <div v-if="selectedLevels.length === 0" class="space-y-2">
        <Icon icon-name="new-job" light size="xl" class="mx-auto" />
        <p class="text-sm text-jet font-bold text-center">
          No job levels have been assigned <br/> to this leave policy yet.
        </p>
        <p class="text-jet text-sm text-center">
          Add one or more job levels to define which <br/> employees are eligible for this policy.
        </p>
      </div>

      <div v-if="selectedLevels.length > 0" class="mt-2">
        <table class="w-full entitlements-table">
          <thead>
            <tr class="border-b border-romanSilver">
              <th class="table-header text-romanSilver" style="text-wrap: nowrap; width: 85px">JOB LEVEL</th>
              <th class="table-header text-romanSilver truncate" style="width: 158px">TOTAL ENTITLEMENT DAYS</th>
              <th v-if="isAnnualLeave && hasCarryForward" class="table-header text-romanSilver" style="width: 145px">CARRY OVER</th>
              <th v-if="isAnnualLeave && hasCarryForward" class="table-header text-romanSilver" style="width: 150px">CARRY OVER EXPIRY</th>
              <th class="table-header text-romanSilver" style="width: 41px;"></th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="level in selectedLevels"
              :key="level.levelId"
              class="border-b border-romanSilver"
            >
              <td class="py-3 px-4 text-xs text-darkPurple">{{ level.name }}</td>
              <td class="py-3 px-4">
                <div v-if="isViewMode" class="text-xs text-darkPurple h-10 flex items-center">
                  {{ level.maximumApplicableDays !== null && level.maximumApplicableDays !== undefined && level.maximumApplicableDays !== '' ? level.maximumApplicableDays : '---' }}
                </div>
                <c-text
                  v-else
                  placeholder="--Enter--"
                  variant="w-full h-10"
                  type="number"
                  min="0"
                  v-model="level.maximumApplicableDays"
                />
              </td>
              <td v-if="isAnnualLeave && hasCarryForward" class="py-3 px-4">
                <div v-if="isViewMode" class="text-xs text-darkPurple h-10 flex items-center">
                  {{ level.carryOverDays !== null && level.carryOverDays !== undefined && level.carryOverDays !== '' ? level.carryOverDays : '---' }}
                </div>
                <div v-else class="flex flex-col gap-1">
                  <c-text
                    placeholder="--Enter--"
                    variant="w-full h-10"
                    type="number"
                    min="0"
                    v-model="level.carryOverDays"
                    :class="hasCarryOverExceededEntitlement(level) ? 'border border-desire' : ''"
                  />
                  <small v-if="hasCarryOverExceededEntitlement(level)" class="text-desire text-xs">
                    Carry over days cannot exceed entitlement days ({{ level.maximumApplicableDays || '---' }})
                  </small>
                </div>
              </td>
              <td v-if="isAnnualLeave && hasCarryForward" class="py-3 px-4">
                <div v-if="isViewMode" class="text-xs text-darkPurple h-10 flex items-center">
                  {{ formatDate(level.carryOverExpiryDate) }}
                </div>
                <div v-else class="border border-romanSilver rounded-md pl-2 w-full">
                  <div 
                    class="flex justify-start items-center gap-1 w-full h-10"
                    v-tooltip.bottom="level.carryOverExpiryDate ? formatFullDate(level.carryOverExpiryDate) : '--'"
                  >
                    <div class="flex justify-center border-r border-romanSilver h-6 pr-1">
                      <Icon icon-name="calendar" size="xs" />
                    </div>
                    <div class="h-10 mt-1 w-full">
                      <datepicker
                        placeholder="--Select--"
                        input-class="date-input"
                        style="width: 100%; outline: none"
                        v-model="level.carryOverExpiryDate"
                      />
                    </div>
                  </div>
                </div>
              </td>
              <td class="py-3 px-4">
                <div class="flex justify-center items-center">
                  <button
                    v-if="hasRowData(level)"
                    @click="copyRowData(level)"
                    class="copy-paste-btn"
                    type="button"
                    :disabled="isViewMode"
                    title="Copy row data"
                  >
                    <Icon icon-name="copy-linear" light size="xs" class="text-flames" />
                  </button>
                  <button
                    v-else
                    @click="pasteRowData(level)"
                    class="copy-paste-btn"
                    type="button"
                    :disabled="isViewMode || !copiedRowData"
                    title="Paste row data"
                  >
                    <Icon light icon-name="paste" size="xs" class="text-mediumSeaGreen" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <div class="flex flex-col gap-3">
      <div v-if="isViewMode" class="mx-2 my-1">
        <label class="text-sm text-jet mb-2 block">
          Select the employee categories to be exempted from this policy.
        </label>
        <div class="h-10 w-full border border-[#CACACA] rounded-md px-3 flex items-center text-sm text-jet bg-gray-50">
          <span v-if="categoryException.length === 0" class="text-[#878E99]">No categories selected</span>
          <span v-else>{{ categoryException.length }} categor{{ categoryException.length === 1 ? 'y' : 'ies' }} selected</span>
        </div>
      </div>
      <CSelect
        v-else
        label="Select the employee categories to be exempted from this policy."
        :placeholder="'Unconfirmed employees'"
        :options="filteredExemptCategories"
        v-model="activeCategory"
        variant="w-full h-10"
        class="h-10 w-full mx-2 my-1"
      />

      <div v-if="categoryException.length > 0" class="flex flex-wrap gap-2 mt-4 mx-2">
        <div
          v-for="(category, index) in categoryException"
          :key="index"
          class="exemption-tag"
        >
          <span class="tag-text">{{ category }}</span>
          <button
            v-if="!isViewMode"
            @click="removeExemptCategory(category)"
            class="tag-close-btn"
            type="button"
          >
            <Icon icon-name="close-icon" size="xms" />
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CCheckbox from "@scelloo/cloudenly-ui/src/components/checkbox";
import CText from "@scelloo/cloudenly-ui/src/components/text";
import CSelect from "@scelloo/cloudenly-ui/src/components/select";
import Icon from "@/components/Icon";
import Datepicker from "vuejs-datepicker";

export default {
  name: "JobLevelEntitlementsStep",
  components: {
    CCheckbox,
    CText,
    CSelect,
    Icon,
    Datepicker,
  },
  props: {
    leaveLevels: {
      type: Array,
      default: () => [],
    },
    selectedLevels: {
      type: Array,
      default: () => [],
    },
    usedLevels: {
      type: Array,
      default: () => [],
    },
    isCreateNew: {
      type: Boolean,
      default: false,
    },
    isViewMode: {
      type: Boolean,
      default: false,
    },
    leaveCategory: {
      type: Object,
      default: () => null,
    },
    employeeCategories: {
      type: Array,
      default: () => [],
    },
    categoryException: {
      type: Array,
      default: () => [],
    },
    hasCarryForward: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      selectAllLevels: false,
      copiedRowData: null,
      isUpdatingFromWatcher: false,
      activeCategory: null,
    };
  },
  computed: {
    displayLevels() {
      let levels = [];
      if (this.leaveLevels && this.leaveLevels.length > 0) {
        levels = this.leaveLevels;
      }

      if (this.isCreateNew && this.usedLevels && this.usedLevels.length > 0) {
        return levels.map(level => ({
          ...level,
          disabled: this.usedLevels.some(usedLevel => usedLevel.id === level.id)
        }));
      }

      return levels;
    },
    availableLevels() {
      return this.displayLevels.filter(level => !level.disabled);
    },
    isSelectAllDisabled() {
      return this.availableLevels.length === 0;
    },
    isAnnualLeave() {
      if (!this.leaveCategory) return false;
      const categoryName = (this.leaveCategory.name || '').toLowerCase().trim();
      return categoryName === 'annual leave';
    },
    availableExemptCategories() {
      const categories = this.employeeCategories || [];
      return categories.map(item =>
        typeof item === "string" ? item : (item.name || item.id)
      );
    },
    filteredExemptCategories() {
      return this.availableExemptCategories.filter(option => !this.categoryException.includes(option));
    },
  },
  watch: {
    activeCategory(newVal) {
      if (newVal) {
        this.handleCategorySelect(newVal);
      }
    },
    selectedLevels: {
      handler(newVal) {
        const shouldBeChecked = newVal.length === this.availableLevels.length && this.availableLevels.length > 0;

        if (this.selectAllLevels !== shouldBeChecked) {
          this.isUpdatingFromWatcher = true;
          this.selectAllLevels = shouldBeChecked;
          this.$nextTick(() => {
            this.isUpdatingFromWatcher = false;
          });
        }
      },
      immediate: true,
      deep: true,
    },
    selectAllLevels: {
      handler(newVal) {
        if (this.isUpdatingFromWatcher) {
          return;
        }

        if (newVal) {

          const newLevels = this.availableLevels.map(level => ({
            levelId: level.id,
            name: level.name,
            maximumApplicableDays: null,
            carryOverDays: null,
            carryOverExpiryDate: null,
          }));
          this.$emit("update:selectedLevels", newLevels);
        } else {
          this.$emit("update:selectedLevels", []);
        }
      },
    },
  },
  methods: {
    handleCategorySelect(category) {
      if (!category) {
        this.activeCategory = null;
        return;
      }

      const categoryString = typeof category === "string" ? category : (category.id || category.name);

      const isAlreadySelected = this.categoryException.includes(categoryString);

      if (!isAlreadySelected) {
        const updatedCategories = [...this.categoryException, categoryString];
        this.$emit('update:categoryException', updatedCategories);
      }

      this.activeCategory = null;
    },
    removeExemptCategory(category) {
      if (this.isViewMode) return;

      const updatedCategories = this.categoryException.filter(cat => cat !== category);
      this.$emit('update:categoryException', updatedCategories);
    },
    toggleLevelSelection(level) {
      if (this.isViewMode || level.disabled) return;

      const currentLevels = [...this.selectedLevels];
      const index = currentLevels.findIndex(l => l.levelId === level.id);

      if (index > -1) {
        currentLevels.splice(index, 1);
      } else {
        currentLevels.push({
          levelId: level.id,
          name: level.name,
          maximumApplicableDays: null,
          carryOverDays: null,
          carryOverExpiryDate: null,
        });
      }

      this.$emit("update:selectedLevels", currentLevels);
    },
    isLevelSelected(levelId) {
      return this.selectedLevels.some(level => level.levelId === levelId);
    },
    hasRowData(level) {
      const hasEntitlementDays = level.maximumApplicableDays !== null && level.maximumApplicableDays !== undefined && level.maximumApplicableDays !== '';

      if (this.isAnnualLeave && this.hasCarryForward) {
        return (
          hasEntitlementDays ||
          (level.carryOverDays !== null && level.carryOverDays !== undefined && level.carryOverDays !== '') ||
          level.carryOverExpiryDate
        );
      }
      return hasEntitlementDays;

    },
    copyRowData(level) {
      this.copiedRowData = {
        maximumApplicableDays: level.maximumApplicableDays !== null && level.maximumApplicableDays !== undefined ? level.maximumApplicableDays : null,
        carryOverDays: level.carryOverDays !== null && level.carryOverDays !== undefined ? level.carryOverDays : null,
        carryOverExpiryDate: level.carryOverExpiryDate ? new Date(level.carryOverExpiryDate) : null,
      };
      this.$toasted.success("Row data copied", { duration: 2000 });
    },
    pasteRowData(level) {
      if (!this.copiedRowData) return;

      const currentLevels = [...this.selectedLevels];
      const index = currentLevels.findIndex(l => l.levelId === level.levelId);

      if (index > -1) {
        const updatedLevel = {
          ...currentLevels[index],
          maximumApplicableDays: this.copiedRowData.maximumApplicableDays,
        };

        if (this.isAnnualLeave && this.hasCarryForward) {
          updatedLevel.carryOverDays = this.copiedRowData.carryOverDays;
          updatedLevel.carryOverExpiryDate = this.copiedRowData.carryOverExpiryDate ? new Date(this.copiedRowData.carryOverExpiryDate) : null;
        }

        currentLevels[index] = updatedLevel;
        this.$emit("update:selectedLevels", currentLevels);
        this.$toasted.success("Row data pasted", { duration: 2000 });
      }
    },
    formatDate(date) {
      if (!date) return '---';
      const dateObj = new Date(date);
      if (Number.isNaN(dateObj.getTime())) return '---';

      const day = String(dateObj.getDate()).padStart(2, '0');
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const year = dateObj.getFullYear();
      return `${day}/${month}/${year}`;
    },
    formatFullDate(date) {
      if (!date) return '';
      const dateObj = new Date(date);
      if (Number.isNaN(dateObj.getTime())) return '';
      return this.$DATEFORMAT(dateObj, 'MMMM dd, yyyy');
    },
    hasCarryOverExceededEntitlement(level) {
      if (!level.carryOverDays || level.carryOverDays === '' || level.carryOverDays === null) return false;
      if (!level.maximumApplicableDays || level.maximumApplicableDays === '' || level.maximumApplicableDays === null) return false;

      const carryOver = Number(level.carryOverDays);
      const entitlement = Number(level.maximumApplicableDays);

      return carryOver > entitlement;
    },
  },
};
</script>

<style scoped>
table {
  border-collapse: collapse;
}

.table-header{
  font-size: 10px;
  font-weight: 900;
  padding: 10px 12px;
  text-transform: uppercase;
  text-align: left;
}

.level-pill {
  min-width: 70px;
  padding: 8px 12px;
  border-radius: 124px;
  border: 1px solid #c2c7d6;
  background: #ffffff;
  color: #321c3b;
  font-size: 14px;
  font-weight: 600;
  transition: all 0.2s ease;
}

.level-pill:hover {
  border-color: var(--dynamic-Background);
}

.level-pill.active {
  background: var(--dynamic-Background);
  color: #ffffff;
  border-color: var(--dynamic-Background);
}

.level-pill.disabled {
  background: #f5f5f5;
  color: #9ca3af;
  border-color: #e5e7eb;
  cursor: not-allowed;
  opacity: 0.6;
}

.level-pill.disabled:hover {
  border-color: #e5e7eb;
}

.line {
  border: 1px solid #C2C7D6;
  width: 100%;
  height: 0;
  margin: 8px auto;
  border-radius: 10px;
  padding: 0;
}

.entitlements-table th,
.entitlements-table td {
  vertical-align: middle;
}

.copy-paste-btn {
  padding: 4px 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s ease;
}

.copy-paste-btn:hover:not(:disabled) {
  background-color: #f5f5f5;
}

.copy-paste-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

:deep(.vdp-datepicker) {
  width: 100%;
}

:deep(.vdp-datepicker__calendar) {
  right: 0 !important;
  left: auto !important;
  max-width: 95vw;
  overflow-x: hidden;
  z-index: 9999;
}

@media (max-width: 640px) {
  :deep(.vdp-datepicker__calendar) {
    right: 8px !important;
    max-width: calc(100vw - 16px);
  }
}

.exemption-tag {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: #f5f5f5;
  border-radius: 6px;
  font-size: 14px;
  color: #321c3b;
}

.tag-text {
  font-weight: 500;
}

.tag-close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  color: #321c3b;
  opacity: 0.7;
  transition: opacity 0.2s ease;
}

.tag-close-btn:hover:not(:disabled) {
  opacity: 1;
}

.tag-close-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}
</style>

